// ========= Copyright 2025-2026 @ Eigent.ai All Rights Reserved. =========
// Licensed under the Apache License, Version 2.0 (the "License");
// you may not use this file except in compliance with the License.
// You may obtain a copy of the License at
//
//     http://www.apache.org/licenses/LICENSE-2.0
//
// Unless required by applicable law or agreed to in writing, software
// distributed under the License is distributed on an "AS IS" BASIS,
// WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
// See the License for the specific language governing permissions and
// limitations under the License.
// ========= Copyright 2025-2026 @ Eigent.ai All Rights Reserved. =========

import { RemoteMarkdown } from '@/components/remote/RemoteMarkdown';
import { TimelineConnector } from '@/components/remote/primitives/TimelineConnector';
import { MorphIcon } from '@/components/ui/morph-icon';
import { cn } from '@/lib/utils';
import type { ActivityItem } from '@web/lib/remoteActivityModel';
import {
  Activity,
  AlertCircle,
  Bot,
  Check,
  CircleHelp,
  Loader2,
  MessageCircle,
  Sparkles,
  Wrench,
} from 'lucide';

function StepIcon({ item }: { item: ActivityItem }) {
  let icon = Activity;
  let className = 'h-3.5 w-3.5';
  if (item.state === 'active') {
    icon = Loader2;
    className += ' animate-spin';
  } else if (item.state === 'failed') {
    icon = AlertCircle;
  } else if (item.kind === 'question') {
    icon = CircleHelp;
  } else if (item.kind === 'user') {
    icon = MessageCircle;
  } else if (item.kind === 'agent') {
    icon = Bot;
  } else if (item.kind === 'tool') {
    icon = Wrench;
  } else if (item.kind === 'result') {
    icon = Sparkles;
  } else if (item.state === 'completed') {
    icon = Check;
  }
  return <MorphIcon icon={icon} className={className} />;
}

export function TimelineStep({
  item,
  last,
}: {
  item: ActivityItem;
  last: boolean;
}) {
  const renderMarkdown = item.kind === 'result' && item.detail;
  const isQuestion = item.kind === 'question';

  return (
    <li className="flex min-w-0 gap-3">
      <TimelineConnector state={item.state} last={last}>
        <StepIcon item={item} />
      </TimelineConnector>
      {isQuestion ? (
        <div className="mb-3 min-w-0 flex-1 pt-0.5">
          <div className="min-w-0 flex-1 rounded-xl border border-solid border-ds-border-status-pending-subtle-default bg-ds-bg-status-pending-subtle-default px-3 py-2.5">
            <p className="m-0 text-body-sm font-medium text-ds-text-neutral-default-default">
              {item.title}
            </p>
            {item.detail && (
              <p className="m-0 mt-0.5 whitespace-pre-wrap break-words text-body-sm text-ds-text-neutral-muted-default">
                {item.detail}
              </p>
            )}
          </div>
        </div>
      ) : (
        <div
          className={cn(
            'min-w-0 flex-1 pb-4 pt-0.5',
            item.kind === 'result' &&
              'mb-1 rounded-xl border border-solid border-ds-border-neutral-subtle-default bg-ds-bg-neutral-subtle-default px-3 py-2.5'
          )}
        >
          <div className="min-w-0">
            <p className="m-0 min-w-0 flex-1 text-body-sm font-medium text-ds-text-neutral-default-default">
              {item.title}
            </p>
          </div>
          {renderMarkdown ? (
            <RemoteMarkdown content={item.detail} className="mt-1" />
          ) : item.detail ? (
            <p className="m-0 mt-0.5 whitespace-pre-wrap break-words text-body-sm text-ds-text-neutral-muted-default">
              {item.detail}
            </p>
          ) : null}
        </div>
      )}
    </li>
  );
}
